import { Share2 } from "lucide-react";
import { MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../app/store";
import DownloadFormatMenu from "../../../../components/ui/download/DownloadFormatMenu";
import { cn } from "../../../../lib/utils";
import { showSuccessToast } from "../../../../lib/toast";
import { downloadInternalReportThunk } from "../../logic/downloadInternalReportThunk";
import { InternalReportConfigEntry } from "./internalReportConfig";
import { ExportFormat } from "../../../../api/download";

interface InternalReportCardProps {
  report: InternalReportConfigEntry;
}

const IconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={(e: MouseEvent) => {
      e.stopPropagation();
      onClick?.();
    }}
    className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E5E5] bg-white text-[#525252] shadow-sm transition-opacity hover:opacity-80"
  >
    {children}
  </button>
);

const InternalReportCard = ({ report }: InternalReportCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleShare = () => {
    navigator.clipboard.writeText(`${window.location.origin}${report.route}`);
    showSuccessToast("Link copied");
  };

  const handleDownload = async (format: ExportFormat) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await dispatch(downloadInternalReportThunk({ reportType: report.id, format }));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      onClick={() => navigate(report.route)}
      className={cn(
        "flex h-full min-h-[150px] cursor-pointer flex-col rounded-xl border border-[#E5E5E5] bg-white p-4 transition-shadow hover:shadow-sm"
      )}
    >
      <h3 className="text-[14px] font-semibold leading-6 text-[#0A0A0A]">{report.title}</h3>
      <p className="mt-1 line-clamp-2 text-sm leading-5 text-[#737373]">{report.description}</p>

      <div className="mt-auto flex items-center justify-between pt-4">
        <div className="flex flex-wrap items-center gap-2">
          {report.tags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              className="rounded-md bg-[#F5F5F4] px-2 py-1 text-xs font-medium text-[#475569]"
            >
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DownloadFormatMenu
            variant="icon"
            loading={isDownloading}
            onSelect={handleDownload}
          />
          <IconButton label="Share" onClick={handleShare}>
            <Share2 size={16} strokeWidth={2} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default InternalReportCard;
